import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  ReactNode,
} from 'react';
import { ThemeProvider } from '@mui/material/styles';
import { buildTheme, ThemeMode } from './muiTheme';

/**
 * Tema claro/oscuro de la app.
 *
 * COMO FUNCIONA
 * -------------
 * El modo se refleja en el atributo `data-theme` de `<html>`. A partir de ahi
 * todo lo demas es automatico:
 *
 *   - `tokens.css` redefine las variables CSS bajo `[data-theme='light']`.
 *   - Tailwind ya apunta a esas variables, asi que `bg-surface` cambia solo.
 *   - MUI recibe el tema que construye `buildTheme(mode)`.
 *
 * Es decir: no hay que repintar componente por componente. Un unico atributo
 * mueve toda la app.
 */

const STORAGE_KEY = 'pmyl-theme-mode';

interface ThemeModeContextValue {
  mode: ThemeMode;
  setMode: (_mode: ThemeMode) => void;
  toggle: () => void;
}

const ThemeModeContext = createContext<ThemeModeContextValue>({
  mode: 'dark',
  setMode: (_mode: ThemeMode) => {},
  toggle: () => {},
});

export const useThemeMode = () => useContext(ThemeModeContext);

const isMode = (value: unknown): value is ThemeMode => value === 'dark' || value === 'light';

/**
 * Modo inicial, en orden de prioridad:
 *
 *   1. Lo que el usuario eligio antes (localStorage).
 *   2. Lo que dice su sistema operativo.
 *   3. Oscuro, que es el tema por defecto del producto.
 */
const resolveInitialMode = (): ThemeMode => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (isMode(stored)) return stored;
  } catch (error) {
    // Modo privado o storage bloqueado: se cae al sistema, no es fatal.
  }

  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
  }

  return 'dark';
};

/**
 * Escribe el atributo en `<html>`.
 *
 * Se llama tambien a nivel de modulo (ver el final del archivo) para que el
 * tema quede aplicado antes del primer render. Si se esperara a un efecto de
 * React, quien tenga guardado el tema claro veria un destello oscuro al
 * recargar.
 */
const applyMode = (mode: ThemeMode) => {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('data-theme', mode);
};

interface ThemeModeProviderProps {
  children: ReactNode;
}

export const ThemeModeProvider = ({ children }: ThemeModeProviderProps) => {
  const [mode, setModeState] = useState<ThemeMode>(resolveInitialMode);

  const setMode = useCallback((next: ThemeMode) => {
    setModeState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch (error) {
      // No poder persistir no debe romper el cambio de tema.
    }
  }, []);

  const toggle = useCallback(() => {
    setModeState((current) => {
      const next: ThemeMode = current === 'dark' ? 'light' : 'dark';
      try {
        localStorage.setItem(STORAGE_KEY, next);
      } catch (error) {
        // Idem.
      }
      return next;
    });
  }, []);

  /**
   * `useLayoutEffect` y no `useEffect`: corre antes de que el navegador pinte,
   * asi que no se ve el cambio de fondo.
   */
  useLayoutEffect(() => {
    applyMode(mode);
  }, [mode]);

  /**
   * Si el usuario nunca eligio, se sigue en vivo lo que haga su sistema
   * operativo. En cuanto toca el toggle, su eleccion manda y se deja de
   * escuchar (porque ya hay valor en localStorage).
   */
  useEffect(() => {
    if (!window.matchMedia) return;

    const query = window.matchMedia('(prefers-color-scheme: light)');
    const onChange = (event: MediaQueryListEvent) => {
      try {
        if (localStorage.getItem(STORAGE_KEY)) return;
      } catch (error) {
        return;
      }
      setModeState(event.matches ? 'light' : 'dark');
    };

    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  /**
   * El tema de MUI se reconstruye al cambiar de modo. `buildTheme` lee los
   * tokens del CSS del modo pedido, no del documento actual, asi que no hay
   * desfase de un render.
   */
  const theme = useMemo(() => buildTheme(mode), [mode]);

  const value = useMemo(() => ({ mode, setMode, toggle }), [mode, setMode, toggle]);

  return (
    <ThemeModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </ThemeModeContext.Provider>
  );
};

/**
 * Aplicacion inmediata al importar el modulo.
 *
 * El destello al recargar se produce entre que el navegador pinta el CSS y que
 * React monta. Ejecutarlo aqui lo elimina en la practica porque `index.tsx`
 * importa este modulo antes de llamar a `root.render`.
 */
applyMode(resolveInitialMode());
